<script setup name="TvbVideoDetailContainer">
import { inject, ref, computed, provide, watch, onMounted, onUnmounted, nextTick } from 'vue'
import TvbVideoDetailPlayer from './../Player/index.vue'
import TvbVideoDetailList from './../List/index.vue'
import TvbVideoDetailNextPlayInfo from './../NextPlayInfo/index.vue'
import { isSafari } from '@/utils/index'

const size = inject('size')
const currentVideo = inject('currentVideo')
// 注入广告加载状态，用于在广告加载完成后重新计算播放器尺寸
const adsLoaded = inject('adsLoaded', ref(true))
// 注入顶层 extraLoaded：等 Player 的 getExtra 完成（category_info.path 就绪）再渲染 LREC 广告
const extraLoaded = inject('extraLoaded', ref(true))
const width = ref(window.innerWidth)
const height = ref(window.innerHeight)

// LREC 广告相关状态
const isShowAd = computed(() => currentVideo.value?.is_show_ad ?? 1)
const videoId = computed(() => currentVideo.value?.id)
const categoryInfo = computed(() => currentVideo.value?.category_info)
// LREC 广告真实高度，广告加载成功后由 GPT 回调提供
const lrecAdHeight = ref(0)
const onLrecAdLoad = (event) => {
  if (event.size?.[1]) {
    lrecAdHeight.value = event.size[1]
  }
}
// 窗口高度，用于判断 LREC 广告类型（lrec1/lrec）
const windowHeight = ref(window.innerHeight)
const updateWindowHeight = () => {
  windowHeight.value = window.innerHeight
}
// LREC 广告显示条件：桌面端（>= 1144px）且未屏蔽广告且扩展信息已加载
const showLrecAd = computed(
  () => videoId.value && isShowAd.value !== 2 && width.value >= 1144 && extraLoaded.value
)
// LREC 广告类型：高度 >= 830px 使用 lrec1（支持 300×600），否则使用 lrec（仅 300×250）
const lrecAdType = computed(() => 'lrec')
const playerWidth = ref('100%')
const playerHeight = ref('100%')
const playerContentRef = ref()
const playerRef = ref()
const playerListRef = ref()
const nextPlayInfoRef = ref()
const nextPlayInfoElRef = ref()
const lmShowList = ref(true)
let resizeObserver = null
let debounceTimer = null

// 获取视频页面根元素，用于设置CSS变量
const getVideoPageElement = () => document.querySelector('.tvb-video-page')

const updateWidth = () => {
  width.value = window.innerWidth
  height.value = window.innerHeight
}

/**
 * 同步更新播放器尺寸到CSS变量
 * 使用同步操作减少延迟，确保响应时间在100ms以内
 * 包含去重逻辑，避免相同尺寸的重复更新导致抖动
 */
const syncPlayerDimensions = (playerElement) => {
  const clientWidth = playerElement.clientWidth
  const clientHeight = playerElement.clientHeight

  // 如果高度为0，保持之前的高度值
  const finalWidth = clientWidth > 0 ? `${clientWidth}px` : playerWidth.value
  const finalHeight = clientHeight > 0 ? `${clientHeight}px` : playerHeight.value

  // 去重：尺寸未变化时跳过更新，避免不必要的 DOM 操作和抖动
  if (finalWidth === playerWidth.value && finalHeight === playerHeight.value) {
    return
  }

  // 直接更新CSS变量，跳过Vue的响应式系统以减少延迟
  const videoPageEl = getVideoPageElement()
  if (videoPageEl) {
    console.log(
      '[Shaka Player] 同步更新播放器尺寸到CSS变量, finalWidth:',
      finalWidth,
      'finalHeight:',
      finalHeight
    )
    videoPageEl.style.setProperty('--player-width', finalWidth)
    videoPageEl.style.setProperty('--player-height', finalHeight)
  }

  // 异步更新响应式数据（用于其他依赖）
  playerWidth.value = finalWidth
  playerHeight.value = finalHeight
}

/**
 * 更新播放器尺寸
 * 优化版本：减少延迟，确保同步时间在100ms以内
 */
const updatePlayerDimensions = () => {
  const playerElement = playerContentRef.value?.querySelector('.tvb-video-detail_player')
  if (!playerElement) return

  // 立即同步更新
  syncPlayerDimensions(playerElement)
}

/**
 * 防抖版本的更新函数（用于resize事件）
 * 使用更短的防抖时间（50ms）
 */
const debouncedUpdateDimensions = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  debounceTimer = setTimeout(() => {
    console.log('[Shaka Player] 防抖更新播放器尺寸 - debouncedUpdateDimensions')
    updatePlayerDimensions()
  }, 50)
}

// 初始化ResizeObserver监听播放器尺寸变化
const initResizeObserver = () => {
  // 先断开旧的observer
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }

  const playerElement = playerContentRef.value?.querySelector('.tvb-video-detail_player')
  if (!playerElement) return

  // 创建新的ResizeObserver
  resizeObserver = new ResizeObserver(() => {
    console.log('[Shaka Player] ResizeObserver 触发更新播放器尺寸')
    // 使用 requestAnimationFrame 确保在浏览器完成布局后再读取尺寸
    // 避免在 flex 布局未稳定时读取到 min-width 的中间值（如 640px）
    requestAnimationFrame(() => {
      updatePlayerDimensions()
    })
  })

  resizeObserver.observe(playerElement)
}

onMounted(() => {
  window.addEventListener('resize', updateWidth)
  window.addEventListener('resize', updateWindowHeight)
  // 延迟初始化ResizeObserver，确保DOM已完全渲染
  nextTick(() => {
    console.log('[Shaka Player] onMounted 初始化更新播放器尺寸')
    initResizeObserver()
    updatePlayerDimensions()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth)
  window.removeEventListener('resize', updateWindowHeight)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
})

const getExtra = (isNotPlay = false) => {
  playerRef.value?.getExtra(isNotPlay)
}

const showList = () => {
  playerListRef.value?.show()
}

provide('playerWidth', playerWidth)
provide('playerHeight', playerHeight)
provide('getExtra', getExtra)
provide('showList', showList)
// 向 Info 等子组件提供 LREC 广告真实高度
provide('lrecAdHeight', lrecAdHeight)

// 监听窗口尺寸和容器引用变化
watch(
  [width, height, playerContentRef],
  () => {
    debouncedUpdateDimensions()
  },
  {
    flush: 'post'
  }
)

// 监听当前视频变化（剧集切换时），重新计算高度
watch(
  () => currentVideo?.value?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      console.log('[Shaka Player] 视频切换, newId:', newId, 'oldId:', oldId)
      // 剧集切换时，立即重新初始化ResizeObserver和计算尺寸
      // 使用nextTick确保Vue的DOM更新已完成
      await nextTick()

      // 立即更新，减少延迟
      console.log('[Shaka Player] 视频切换后初始化更新播放器尺寸')
      initResizeObserver()
      updatePlayerDimensions()

      // Safari浏览器额外计算一次，确保高度正确（延迟100ms）
      if (isSafari()) {
        setTimeout(() => {
          console.log('[Shaka Player] Safari 延迟更新播放器尺寸')
          updatePlayerDimensions()
        }, 100)
      }
    }
  },
  { flush: 'post' }
)

// 监听广告加载状态变化，广告加载完成后重新计算播放器尺寸
// 解决广告位异步加载导致的播放器尺寸计算不准确问题
watch(adsLoaded, async (loaded) => {
  if (loaded) {
    console.log('[Shaka Player] 广告加载完成，重新计算播放器尺寸')
    // 等待 DOM 更新完成后再计算尺寸
    await nextTick()
    // 重新初始化 ResizeObserver 以监听新的布局变化
    initResizeObserver()
    // 立即更新播放器尺寸
    updatePlayerDimensions()
    // 额外延迟一次计算，确保广告加载引起的页面重排完全结束
    setTimeout(() => {
      updatePlayerDimensions()
    }, 100)
  }
})
</script>

<template>
  <div class="tvb-video-detail_container" :class="[size]">
    <div class="tvb-video-detail_container-content" ref="playerContentRef">
      <tvb-video-detail-player :key="currentVideo?.hash || currentVideo?.id" ref="playerRef" />
      <Teleport
        v-if="nextPlayInfoElRef"
        :to="nextPlayInfoElRef"
        :disabled="!['s', 'xs'].includes(size)"
      >
        <tvb-video-detail-next-play-info
          v-if="(width <= 1143 && !lmShowList) || ['s', 'xs'].includes(size)"
          ref="nextPlayInfoRef"
          @click="playerListRef?.show"
        />
      </Teleport>
      <tvb-video-detail-list
        ref="playerListRef"
        @lm-show-list-change="(show) => (lmShowList = show)"
      />
      <!-- LREC 广告：位于播放列表下方居中，仅桌面端（>= 1144px）显示 -->
      <tvb-ads
        v-if="showLrecAd"
        class="tvb-video-detail_container-lrec"
        :ad-type="lrecAdType"
        :position="14"
        :unit="`/${videoId}`"
        :show-ad="isShowAd !== 2"
        :params="{
          content_type: 'video',
          vtype: 'vod',
          sub_category_path: categoryInfo?.path || 'x',
          video_id: videoId || 'x'
        }"
        @load="onLrecAdLoad"
      />
    </div>
    <slot :playerWidth="playerWidth" />
    <div class="tvb-video-detail_container-next-play-info" ref="nextPlayInfoElRef"></div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-video-detail_container {
  display: flex;
  flex-direction: column;

  .tvb-video-detail_container-content {
    display: flex;
    gap: 24px;
    // 使用align-items: flex-start确保子元素从顶部对齐，避免高度被拉伸
    align-items: flex-start;
    // 相对定位，用于 LREC 广告绝对定位
    position: relative;

    @media (min-width: 1144px) {
      justify-content: center;
    }

    // LREC 广告：使用绝对定位在播放列表下方，相对于播放列表居中
    // 播放列表宽度 400px，LREC 宽度 300px，居中偏移 (400 - 300) / 2 = 50px
    .tvb-video-detail_container-lrec {
      display: none;
      position: absolute;
      right: 50px;
      top: 100%;
      margin-top: 16px;
      width: 300px;
      min-height: 250px;
    }

    @media (min-width: 1144px) {
      .tvb-video-detail_container-lrec {
        display: block;
      }
    }
  }

  @media (max-width: 1440px) {
    &.xl {
      .tvb-video-detail_container-content {
        gap: 24px;
      }
    }
  }

  &.l,
  &.m {
    .tvb-video-detail_container-content {
      gap: 24px;
    }

    @media (max-width: 1143px) {
      .tvb-video-detail_container-content {
        flex-direction: column;
        gap: 0;
      }
    }
  }

  &.s,
  &.xs {
    .tvb-video-detail_container-content {
      flex-direction: column;
      gap: 0;
      position: sticky;
      top: 84px;
      z-index: 9;
    }
  }

  // &.m {
  //     .tvb-video-detail_container-content {
  //         top: 72px;
  //     }
  // }

  .tvb-video-detail_container-next-play-info {
    position: fixed;
    // TC-1482：避开 iOS Home 键安全区，最终视觉底部 ≥ 20px + safe-area（原固定 34px）
    bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    left: 0;
    width: 100%;
    // 收起条左右 x=12px（保持）
    padding: 0 12px;
    z-index: 2;
  }
}
</style>
